import { useMutation } from '@apollo/client';

import type {
  ArticlesError,
  ContentError,
} from '@xing-com/crate-common-graphql-types';
import { ContentArticleStatus } from '@xing-com/crate-common-graphql-types';

import { EntityPagesArticleModuleArticlesArticleDisableDocument } from '../graphql/mutations/entity-pages-article-module-articles-article-disable.gql-types';
import { EntityPagesArticleModuleContentArticleDisableDocument } from '../graphql/mutations/entity-pages-article-module-content-article-disable.gql-types';
import type { ArticleCardView } from '../types/types';

type DisableArticleParams = {
  article: ArticleCardView;
};

export const useDisableArticleMutation = (options?: {
  onCompleted?: (article: ArticleCardView) => void;
  onError?: (error: ContentError | ArticlesError | Error) => void;
}) => {
  const [handleContentArticleDisable, contentArticleState] = useMutation(
    EntityPagesArticleModuleContentArticleDisableDocument
  );
  const [handleArticlesArticleDisable, articlesArticleState] = useMutation(
    EntityPagesArticleModuleArticlesArticleDisableDocument
  );
  const setArticle = (article: ArticleCardView) => ({
    ...article,
    publicationState: ContentArticleStatus.Disabled,
  });

  const handleDisableArticle = ({ article }: DisableArticleParams) => {
    if (article.typename === 'ContentArticle') {
      handleContentArticleDisable({
        variables: {
          input: { globalId: article.globalId },
        },
        onCompleted: (data) => {
          const error = data.contentDisableArticle?.error;
          if (error) {
            options?.onError?.(error);
          } else {
            options?.onCompleted?.(setArticle(article));
          }
        },
        onError: (error) => {
          options?.onError?.(error);
        },
      });
    } else {
      handleArticlesArticleDisable({
        variables: {
          input: { articleGlobalId: article.globalId },
        },
        onCompleted: (data) => {
          const error =
            data.articlesDisableArticle?.__typename === 'ArticlesError'
              ? data.articlesDisableArticle
              : null;
          if (error) {
            options?.onError?.(error);
          } else {
            options?.onCompleted?.(setArticle(article));
          }
        },
        onError: (error) => {
          options?.onError?.(error);
        },
      });
    }
  };

  return {
    handleDisableArticle,
    loading: contentArticleState.loading || articlesArticleState.loading,
  };
};
